/**
 * C-Bridge Publieke Landing Page
 *
 * Secties: Hero, Probleem, Oplossing, Features, Testimonial, Pricing, Demo formulier
 * Geen authenticatie vereist — publiek toegankelijk via /landing
 */

import { useEffect, useState } from 'react';
import {
  MessageSquare,
  Truck,
  Users,
  BarChart3,
  Clock,
  CheckCircle,
  Star,
  ArrowRight,
  Phone,
  Mail,
  Building2,
  ChevronDown,
} from 'lucide-react';
import { KordaroLogo, KordaroMark } from '../../../core/shared/components/KordaroLogo.js';

// ─── SEO ────────────────────────────────────────────────────────────────────

function useSeo() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Kordaro — Minder inbound telefoons, meer tevreden klanten';

    const setMeta = (name: string, content: string, prop?: boolean) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Kordaro is het SaaS platform voor keukenwinkels. Verminder inbound telefoons met 30-50% door klanten automatisch op de hoogte te houden van hun dossier.');
    setMeta('og:title', 'Kordaro — Minder inbound telefoons, meer tevreden klanten', true);
    setMeta('og:description', 'Het platform dat keukenwinkels helpt om klantcommunicatie te automatiseren. Tijdlijn, communicatie, logistiek en CRM in één.', true);
    setMeta('og:type', 'website', true);
    setMeta('og:url', 'https://app.c-bridge.app/landing', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Kordaro — Minder inbound telefoons, meer tevreden klanten');
    setMeta('twitter:description', 'Het platform dat keukenwinkels helpt om klantcommunicatie te automatiseren.');
    setMeta('og:image', 'https://app.c-bridge.app/og-image.png', true);
    setMeta('og:locale', 'nl_BE', true);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://app.c-bridge.app/landing');

    return () => {
      document.title = prev;
      canonical?.remove();
    };
  }, []);
}

// ─── Components ─────────────────────────────────────────────────────────────

const FEATURES = [
  {
    icon: Clock,
    title: 'Dossier tijdlijn',
    desc: 'Klanten volgen hun keukenproject live: van bestelling tot plaatsing. Geen telefoontje meer nodig.',
  },
  {
    icon: MessageSquare,
    title: 'Automatische communicatie',
    desc: 'Email, SMS en WhatsApp updates bij elke statuswijziging — automatisch verstuurd, nul manueel werk.',
  },
  {
    icon: Truck,
    title: 'Logistiek overzicht',
    desc: 'Leveringen, leveranciersbonnen en apparatenstatus in één scherm. Nooit meer zoeken.',
  },
  {
    icon: Users,
    title: 'CRM & pipeline',
    desc: 'Van lead tot gewonnen offerte. Opvolging, afspraken en offertebeheer volledig geïntegreerd.',
  },
  {
    icon: BarChart3,
    title: 'Dashboard & KPIs',
    desc: 'Omzet, conversie en verkopersprestaties altijd up-to-date. Export naar Excel met één klik.',
  },
  {
    icon: Phone,
    title: 'Buitendienst mobiel',
    desc: 'Plaatsers werken mobiel: checklist, foto\'s, handtekening en werkbon — alles in de app.',
  },
];

const PRICING_TIERS = [
  {
    name: 'Starter',
    price: '55',
    desc: 'Per gebruiker/maand',
    features: ['CRM & dossiers', 'Klantportaal', 'Automatische emails', 'Logistiek overzicht'],
    cta: 'Start gratis demo',
    highlight: false,
  },
  {
    name: 'Groei',
    price: '79',
    desc: 'Per gebruiker/maand',
    features: ['Alles van Starter', 'WhatsApp Business', 'Marketing module', 'Planning kalender', 'Service & garantie'],
    cta: 'Start gratis demo',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Op maat',
    desc: 'Franchise & meerdere winkels',
    features: ['Alles van Groei', 'Multi-winkel dashboard', 'Databus WMS koppeling', 'Dedicated support', 'SLA garantie'],
    cta: 'Neem contact op',
    highlight: false,
  },
];

// ─── Demo Form ───────────────────────────────────────────────────────────────

interface FormState {
  voornaam: string;
  achternaam: string;
  winkel: string;
  email: string;
  telefoon: string;
}

function DemoForm({ id }: { id?: string }) {
  const [form, setForm] = useState<FormState>({ voornaam: '', achternaam: '', winkel: '', email: '', telefoon: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gdprConsent, setGdprConsent] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.voornaam.trim() || !form.achternaam.trim() || !form.email.trim()) {
      setError('Vul naam en email in');
      return;
    }
    if (!gdprConsent) {
      setError('Gelieve akkoord te gaan met het privacybeleid');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/public/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Fout bij verzenden');
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-14 h-14 text-green-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Aanvraag ontvangen!</h3>
        <p className="text-gray-400">We nemen binnen 24 uur contact met u op voor een persoonlijke demo.</p>
      </div>
    );
  }

  return (
    <form id={id} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Voornaam *</label>
          <input
            name="voornaam"
            value={form.voornaam}
            onChange={handleChange}
            placeholder="Jan"
            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand transition-colors"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Achternaam *</label>
          <input
            name="achternaam"
            value={form.achternaam}
            onChange={handleChange}
            placeholder="Peeters"
            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand transition-colors"
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Building2 className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
          Naam winkel / bedrijf
        </label>
        <input
          name="winkel"
          value={form.winkel}
          onChange={handleChange}
          placeholder="IXINA Brussel"
          className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Mail className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
          Email *
        </label>
        <input
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          placeholder="jan@uwwinkel.be"
          className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand transition-colors"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          <Phone className="inline w-3.5 h-3.5 mr-1 mb-0.5" />
          Telefoonnummer
        </label>
        <input
          name="telefoon"
          type="tel"
          value={form.telefoon}
          onChange={handleChange}
          placeholder="+32 470 00 00 00"
          className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand transition-colors"
        />
      </div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={gdprConsent}
          onChange={(e) => setGdprConsent(e.target.checked)}
          className="mt-1 accent-brand"
        />
        <span className="text-sm text-gray-400">
          Ik ga akkoord met het{' '}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-brand underline hover:text-[#b8994f]">
            privacybeleid
          </a>{' '}
          en geef toestemming om gecontacteerd te worden.
        </span>
      </label>
      {error && (
        <p className="text-red-400 text-sm">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !gdprConsent}
        className="w-full py-3 px-6 bg-brand hover:bg-[#b8994f] disabled:opacity-60 text-[#1a1a1a] font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {loading ? (
          <span className="w-5 h-5 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
        ) : (
          <>Demo aanvragen <ArrowRight className="w-4 h-4" /></>
        )}
      </button>
      <p className="text-xs text-gray-500 text-center">Gratis demo · Geen verplichtingen · Antwoord binnen 24u</p>
    </form>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function LandingPage() {
  useSeo();

  return (
    <div className="min-h-screen bg-[#141414] text-white antialiased">

      {/* ── Nav ── */}
      <nav className="sticky top-0 z-50 bg-[#141414]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <KordaroLogo variant="ivory" />
          <a
            href="#demo"
            className="px-4 py-1.5 bg-brand hover:bg-[#b8994f] text-[#1a1a1a] font-semibold rounded-lg text-sm transition-colors"
          >
            Demo aanvragen
          </a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 overflow-hidden">
        {/* Subtle gradient blob */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-brand/8 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/15 border border-brand/30 text-brand text-xs font-medium mb-6">
            <Star className="w-3.5 h-3.5" />
            Het platform voor keukenretailers
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Verminder inbound{' '}
            <span className="text-brand">telefoons</span>
            <br />
            met <span className="text-brand">30–50%</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Kordaro houdt uw klanten automatisch op de hoogte van hun keukenproject.
            Van bestelling tot plaatsing — transparant, efficiënt, tevreden.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#demo"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand hover:bg-[#b8994f] text-[#1a1a1a] font-bold rounded-lg transition-colors text-base"
            >
              Demo aanvragen <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/20 hover:border-brand/50 text-gray-300 hover:text-white rounded-lg transition-colors text-base"
            >
              Meer ontdekken <ChevronDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <div className="bg-[#1e1e1e] border-y border-white/5 py-8 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { val: '30–50%', label: 'minder inbound telefoons' },
            { val: '< 1 dag', label: 'onboarding tijd' },
            { val: '29+', label: 'leveranciers automatisch gekoppeld' },
            { val: '99.6%', label: 'foutloze documentverwerking' },
          ].map(s => (
            <div key={s.label}>
              <div className="text-2xl font-extrabold text-brand">{s.val}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Probleem ── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Uw klanten bellen constant voor<br />
            <span className="text-brand">statusupdates</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10">
            "Wanneer wordt mijn keuken geleverd?" — "Zijn de apparaten al binnen?" — "Wanneer komt de plaatser?"
            Uw team besteedt uren per week aan telefoontjes die volledig vermijdbaar zijn.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
            {[
              { title: 'Silo\'s per afdeling', desc: 'Verkoop, logistiek en plaatsing werken elk in hun eigen systeem. Klanten vallen tussen de mazen.' },
              { title: 'Manueel opvolgen', desc: 'Status emails, herinneringen en updates worden vergeten of handmatig verstuurd — tijdrovend en foutgevoelig.' },
              { title: 'Geen zichtbaarheid', desc: 'Klanten weten niet waar hun project staat. Gebrek aan informatie = angst = telefoontjes.' },
            ].map(p => (
              <div key={p.title} className="p-5 rounded-xl bg-red-500/5 border border-red-500/20">
                <h3 className="font-semibold text-red-300 mb-2">{p.title}</h3>
                <p className="text-gray-400 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Oplossing ── */}
      <section className="py-16 px-4 sm:px-6 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Kordaro houdt uw klanten{' '}
            <span className="text-brand">automatisch</span>{' '}
            op de hoogte
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Van SMART PDF import tot WhatsApp notificaties — Kordaro verbindt alle documenten, systemen en
            mensen in één platform. Uw klant ziet live waar zijn project staat, zonder dat uw team iets
            hoeft te doen.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap text-sm text-gray-400">
            {['Automatische statusemails', 'Klantportaal live tracking', 'WhatsApp Business integratie', 'SMART PDF import'].map(f => (
              <span key={f} className="flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-brand" /> {f}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Alles wat uw winkel nodig heeft</h2>
            <p className="text-gray-400">Eén platform voor de volledige customer journey van uw keukenklant.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-xl bg-[#1e1e1e] border border-white/8 hover:border-brand/30 transition-colors group"
              >
                <div className="w-10 h-10 rounded-lg bg-brand/15 flex items-center justify-center mb-4 group-hover:bg-brand/25 transition-colors">
                  <Icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="font-semibold text-white mb-2">{title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonial ── */}
      <section className="py-16 px-4 sm:px-6 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-5 h-5 fill-brand text-brand" />)}
          </div>
          <blockquote className="text-xl sm:text-2xl font-medium text-white italic mb-6">
            "Onze klanten bellen niet meer voor updates — ze bekijken gewoon hun portaal.
            De tijdsbesparing voor ons team is enorm."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand/20 flex items-center justify-center text-brand font-bold">
              IB
            </div>
            <div className="text-left">
              <div className="text-sm font-semibold text-white">IXINA Boortmeerbeek</div>
              <div className="text-xs text-gray-500">Eerste Kordaro klant</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Transparante prijzen</h2>
            <p className="text-gray-400">Vanaf <span className="text-brand font-semibold">€55/gebruiker/maand</span> — alles inbegrepen, geen verborgen kosten.</p>
            <p className="text-xs text-gray-500 mt-2">Prijzen excl. BTW</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {PRICING_TIERS.map(tier => (
              <div
                key={tier.name}
                className={`p-6 rounded-xl border flex flex-col ${
                  tier.highlight
                    ? 'bg-brand/10 border-brand/40 ring-1 ring-brand/30'
                    : 'bg-[#1e1e1e] border-white/10'
                }`}
              >
                {tier.highlight && (
                  <div className="text-xs font-bold text-brand uppercase tracking-wider mb-3">Meest gekozen</div>
                )}
                <h3 className="text-lg font-bold mb-1">{tier.name}</h3>
                <div className="mb-1">
                  <span className="text-3xl font-extrabold text-brand">
                    {tier.price.startsWith('€') || !isNaN(Number(tier.price)) ? `€${tier.price}` : tier.price}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-5">{tier.desc}</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-300">
                      <CheckCircle className="w-4 h-4 text-brand flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <a
                  href="#demo"
                  className={`block text-center py-2.5 px-4 rounded-lg font-semibold text-sm transition-colors ${
                    tier.highlight
                      ? 'bg-brand hover:bg-[#b8994f] text-[#1a1a1a]'
                      : 'border border-white/20 hover:border-brand/50 text-gray-300 hover:text-white'
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Demo formulier ── */}
      <section id="demo" className="py-20 px-4 sm:px-6 bg-[#1a1a1a]">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Demo aanvragen</h2>
            <p className="text-gray-400">
              Laat uw gegevens achter en we plannen een persoonlijke demo in — op uw tijd, op uw locatie.
            </p>
          </div>
          <div className="bg-[#1e1e1e] border border-white/10 rounded-2xl p-6 sm:p-8">
            <DemoForm id="demo-form" />
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/5 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600">
          <KordaroLogo variant="ivory" />
          <span>© {new Date().getFullYear()} Mare BV — Alle rechten voorbehouden</span>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <a href="/legal/privacy">Privacybeleid</a> · <a href="/legal/terms">Algemene voorwaarden</a> · <a href="/legal/cookies">Cookie beleid</a>
          </div>
          <a href="mailto:sanju@c-bridge.app" className="hover:text-gray-400 transition-colors">sanju@c-bridge.app</a>
        </div>
      </footer>

    </div>
  );
}
